import React, { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart } from "@mui/icons-material";
import CustomDataGrid from '../../../../components/ui/customDataGrid/customDataGrid.component';
import { ORDERED_PARTS_ENDPOINT, PART_STATUS_OPTIONS } from '../../../../consts';
import styles from './orderedParts.module.css';
import { useHasPermission } from '../../../../contexts/auth';
import { formatMoney, formatQuantity } from '../../../../utils';
import {
  containsStringFilterOperators,
  equalsNumberFilterOperators,
  exactDateFilterOperators,
  singleSelectFilterOperators,
} from '../../../../components/ui/customDataGrid/filterOperators';

export const OrderedParts = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const canViewOrderedParts = useHasPermission("service.view_orderedpart");

  const STATUS_OPTIONS = useMemo(
    () => PART_STATUS_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t]
  );

  const handleRowClick = (orderedPartId: string) => {
    navigate(`/service/stock-status/${id}/ordered-parts/${orderedPartId}`);
  };

  const columns: GridColDef[] = [
    {
      field: "number",
      headerName: t("Number"),
      filterOperators: containsStringFilterOperators(),
    },
    {
      field: "status",
      headerName: t("Status"),
      type: "singleSelect",
      valueOptions: STATUS_OPTIONS,
      filterOperators: singleSelectFilterOperators(),
      valueGetter: (value, row) => row.status,
      renderCell: (params) => params.row.status_display,
    },
    {
      field: "delivered_at",
      headerName: t("Delivered At"),
      editable: false,
      filterOperators: exactDateFilterOperators(),
      valueGetter: (value) => value || "-",
    },
    {
      field: "price_net",
      headerName: t("Price Net"),
      editable: false,
      filterable: false,
      valueGetter: (value, row) =>
        `${formatMoney(row.price_net)} (+${formatQuantity(row.vat)}% ${t("VAT")})`,
    },
    {
      field: "availability",
      headerName: t("Availability"),
      sortable: false,
      filterable: false,
      valueGetter: (value, row) =>
        `${formatQuantity(row.availability)} / ${formatQuantity(row.quantity)}`,
    },
    {
      field: "total",
      headerName: t("Total Cost"),
      editable: false,
      type: "number",
      filterOperators: equalsNumberFilterOperators(),
      // valueFormatter (not valueGetter) keeps the raw numeric value for
      // filtering/sorting and only changes the displayed string - unlike the
      // old valueGetter here, which broke both.
      valueFormatter: (value) => formatMoney(value),
    },
    {
      field: "ordered_by",
      headerName: t("Ordered By"),
      sortable: false,
      filterable: false,
      valueGetter: (value, row) => {
        const orderedBy = row.ordered_by;
        if (!orderedBy) return "-";
        const name = [orderedBy.first_name, orderedBy.last_name].filter(Boolean).join(' ');
        return name || orderedBy.email || "-";
      }
    },
  ];

  if (!canViewOrderedParts) {
    return null;
  }

  const orderedPartsUrl = id ? `${ORDERED_PARTS_ENDPOINT}?part=${id}` : ORDERED_PARTS_ENDPOINT;

  return (
    <Box className={styles.container}>
      <CustomDataGrid
        icon={<ShoppingCart />}
        title={t('Ordered Parts')}
        url={orderedPartsUrl}
        gridSettingsKey="ORDERED_PARTS"
        columns={columns}
        // Using a part consumes an order's availability and can flip it to
        // "Used", so this table has to react to the used-part events too now
        // that both lists sit on the same screen.
        refreshEventName={["orderedPartAdded", "orderedPartUpdated", "usedPartAdded", "usedPartUpdated"]}
        onRowClick={handleRowClick}
      />
    </Box>
  );
};
